import React, { useState } from 'react';
import {
  Paper, Box, Typography, Button, FormControl, Select, MenuItem, CircularProgress, Divider,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TopicOutlinedIcon from '@mui/icons-material/TopicOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import { ref, get, child } from 'firebase/database';
import { rtdb, auth, RTDB_URL } from '../firebase-config';
import { DIFFICULTY_PATTERNS, dedupeQuestions, pickForExamType, spreadByType, questionTopic } from '../utils/examDifficulty';
import { FieldLabel, selectSx, generateId } from './CreateExam';

const QUESTION_COUNT = 20;
const DURATION_MINUTES = 30; // 1.5 min per question
const CLASSES = ['11', '12'];
const NOT_CHAPTERS = ['chapter_details', 'undefined', 'null'];

// Shallow REST read returns only the child keys, not the (large) question data under them
const childKeys = async (path) => {
  try {
    const token = await auth.currentUser?.getIdToken();
    const url = `${RTDB_URL}/${path.split('/').map(encodeURIComponent).join('/')}.json?shallow=true${token ? `&auth=${token}` : ''}`;
    const res = await fetch(url);
    const data = res.ok ? await res.json() : null;
    return data && typeof data === 'object' ? Object.keys(data) : [];
  } catch { return []; }
};

const readList = async (path) => {
  const snap = await get(child(ref(rtdb), path));
  if (!snap.exists()) return [];
  const data = snap.val();
  return (Array.isArray(data) ? data : Object.values(data)).filter(Boolean);
};

export default function PracticeExam({ onNextClick }) {
  const [classLevel, setClassLevel] = useState('');
  const [subjects, setSubjects] = useState([]); // [{ name, base: 'prod/11/Physics' }]
  const [subject, setSubject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState('');
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState(null);
  const [examType, setExamType] = useState('KCET');
  const [loading, setLoading] = useState(''); // which list is loading
  const [generating, setGenerating] = useState(false);

  const chooseClass = async (cls) => {
    setClassLevel(cls); setSubject(null); setChapter(''); setTopic(null);
    setSubjects([]); setChapters([]); setTopics([]);
    setLoading('subjects');
    const found = new Map();
    for (const root of [`prod/${cls}`, `prod/Class ${cls}`]) {
      (await childKeys(root)).forEach(name => { if (!found.has(name)) found.set(name, `${root}/${name}`); });
    }
    setSubjects([...found].map(([name, base]) => ({ name, base })).sort((a, b) => a.name.localeCompare(b.name)));
    setLoading('');
  };

  const chooseSubject = async (name) => {
    const sub = subjects.find(s => s.name === name);
    setSubject(sub); setChapter(''); setTopic(null); setChapters([]); setTopics([]);
    setLoading('chapters');
    setChapters((await childKeys(`${sub.base}/chapters`)).filter(c => !NOT_CHAPTERS.includes(c)).sort());
    setLoading('');
  };

  const chooseChapter = async (ch) => {
    setChapter(ch); setTopic(null); setTopics([]);
    setLoading('topics');
    try {
      const list = await readList(`${subject.base}/chapters/${ch}/topic_details`);
      setTopics(list.filter(t => t.label && t.value !== undefined).sort((a, b) => String(a.label).localeCompare(String(b.label))));
    } catch (err) {
      console.error('Topic fetch error:', err);
    }
    setLoading('');
  };

  // Topic is optional: none = whole chapter (chapter-wise), one = topic-wise
  const isReady = classLevel && subject && chapter;

  const handleGenerate = async () => {
    if (!isReady) return;
    setGenerating(true);
    try {
      let questions = await readList(`${subject.base}/chapters/${chapter}/questions`);
      if (questions.length === 0) questions = await readList(`${subject.base}/chapters/${chapter}/chapter_details/questions`);

      const chapterPool = dedupeQuestions(questions);
      const pool = topic ? chapterPool.filter(q => questionTopic(q) === Number(topic.value)) : chapterPool;
      const picked = pickForExamType(pool, QUESTION_COUNT, examType, spreadByType)
        .map(q => ({ ...q, chapterName: chapter }));

      if (picked.length === 0) {
        alert(`No questions found for this ${topic ? 'topic' : 'chapter'} and exam type. Try another ${topic ? 'topic' : 'chapter'} or exam type.`);
        return;
      }

      onNextClick({
        id: generateId(),
        name: `Practice — ${subject.name}`,
        examName: `Practice — ${subject.name}`,
        duration: DURATION_MINUTES,
        totalQuestions: picked.length,
        templateId: 'practice',
        class: classLevel,
        type: 'create_own_exam',
        granularity: topic ? 'topic' : 'chapter',
        chapterSelections: { [subject.name]: chapter },
        topicSelections: topic ? { [subject.name]: topic.label } : null,
        difficultyPattern: examType,
        subjects: { [subject.name]: picked },
      });
    } catch (err) {
      console.error('Practice exam generation error:', err);
      alert('Error generating exam. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const placeholder = (text) => <Typography component="span" sx={{ color: '#94a3b8' }}>{text}</Typography>;

  return (
    <Box sx={{ maxWidth: 1080 }}>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography component="h1" sx={{ fontWeight: 700, color: '#0f172a', fontSize: { xs: '26px', md: '34px' }, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
          Practice Exam
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: { xs: '14px', md: '16px' }, mt: 1, maxWidth: 560, lineHeight: 1.6 }}>
          Practise a whole chapter or a single topic: {QUESTION_COUNT} questions in {DURATION_MINUTES} minutes.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(15,23,42,0.04)', p: { xs: 2.5, md: 4 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 3.5 }}>
          <Box>
            <FieldLabel Icon={SchoolIcon}>Class</FieldLabel>
            <FormControl fullWidth>
              <Select displayEmpty value={classLevel} onChange={e => chooseClass(e.target.value)} sx={selectSx}
                renderValue={v => v ? `Class ${v}` : placeholder('Select class…')}>
                {CLASSES.map(c => <MenuItem key={c} value={c}>Class {c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel Icon={MenuBookIcon}>Subject</FieldLabel>
            <FormControl fullWidth disabled={!classLevel || loading === 'subjects'}>
              <Select displayEmpty value={subject?.name || ''} onChange={e => chooseSubject(e.target.value)} sx={selectSx}
                renderValue={v => v || placeholder(loading === 'subjects' ? 'Loading subjects…' : 'Select subject…')}>
                {subjects.map(s => <MenuItem key={s.name} value={s.name}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel Icon={DescriptionOutlinedIcon}>Chapter</FieldLabel>
            <FormControl fullWidth disabled={!subject || loading === 'chapters'}>
              <Select displayEmpty value={chapter} onChange={e => chooseChapter(e.target.value)} sx={selectSx}
                renderValue={v => v || placeholder(loading === 'chapters' ? 'Loading chapters…' : 'Select chapter…')}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
                {chapters.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel Icon={TopicOutlinedIcon}>
              Topic <Typography component="span" sx={{ fontSize: '13px', fontWeight: 500, color: '#94a3b8' }}>(optional)</Typography>
            </FieldLabel>
            <FormControl fullWidth disabled={!chapter || loading === 'topics' || topics.length === 0}>
              <Select displayEmpty value={topic ? String(topic.value) : ''} sx={selectSx}
                onChange={e => setTopic(topics.find(t => String(t.value) === e.target.value) || null)}
                renderValue={v => v ? topic?.label : placeholder(
                  loading === 'topics' ? 'Loading topics…'
                    : !chapter ? 'Select topic…'
                      : topics.length === 0 ? 'Whole chapter (no topics listed)'
                        : 'All topics (whole chapter)'
                )}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
                <MenuItem value="">All topics (whole chapter)</MenuItem>
                {topics.map(t => (
                  <MenuItem key={t.value} value={String(t.value)}>
                    {t.label}
                    {t.top_qcount ? <Typography component="span" sx={{ ml: 1, fontSize: '12px', color: '#94a3b8' }}>{t.top_qcount} Qs</Typography> : null}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <FieldLabel Icon={SpeedIcon}>Exam Type</FieldLabel>
            <Box role="radiogroup" aria-label="Exam type" sx={{ display: 'inline-flex', bgcolor: '#f1f5f9', borderRadius: '12px', p: '4px', gap: '4px' }}>
              {Object.keys(DIFFICULTY_PATTERNS).map(key => {
                const active = examType === key;
                return (
                  <Box
                    key={key}
                    role="radio"
                    aria-checked={active}
                    tabIndex={0}
                    onClick={() => setExamType(key)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExamType(key); } }}
                    sx={{
                      px: { xs: 2, sm: 2.5 }, py: 1.2, borderRadius: '10px', cursor: 'pointer', userSelect: 'none',
                      bgcolor: active ? '#fff' : 'transparent',
                      boxShadow: active ? '0 2px 8px rgba(0,0,0,0.09)' : 'none',
                      color: active ? '#1d4ed8' : '#64748b',
                      fontSize: '13px', fontWeight: active ? 800 : 600,
                      transition: 'all 0.18s',
                      '&:focus-visible': { outline: '2px solid #1d4ed8', outlineOffset: 2 },
                    }}
                  >
                    {key}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: '#f1f5f9' }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'flex-end', gap: 2 }}>
          {!isReady && (
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textAlign: { xs: 'center', md: 'right' } }}>
              {!classLevel ? '① Choose a class' : !subject ? '② Choose a subject' : '③ Choose a chapter'}
            </Typography>
          )}
          <Button
            variant="contained"
            disabled={!isReady || generating}
            onClick={handleGenerate}
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              py: 1.5, px: 4, minWidth: { md: 280 }, borderRadius: '12px', bgcolor: '#1d4ed8', boxShadow: 'none',
              fontWeight: 700, fontSize: '15px', textTransform: 'none',
              '&:hover': { bgcolor: '#1e40af', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
            }}
          >
            {generating ? 'Preparing Exam…' : 'Start Practice'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
